import { DocumentMapping } from "@/configuration/document-mappings/types";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { Classification } from "@/src/models/constants/classifications";
import { V2WorkflowType } from "@/src/models/constants/workflow-types";

export enum FidAssignmentStrategy {
  DEPARTMENT_STAFF_BUCKET = "department_staff_bucket",
}

export type FidDocumentMapping = DocumentMapping & { assignmentStrategy?: FidAssignmentStrategy };

export const FID_MAPPINGS: Partial<Record<Classification, FidDocumentMapping>> = {
  [Classification.CLEARANCE_REQUEST_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "fidneedslabs",
    displayName: "Cardiac clearances",
    documentLabel: "Cardiac Clearance",
    documentTypeId: 309005,
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.CLEARANCE_REQUEST_RESPONSE,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.DME_ORDER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: FidAssignmentStrategy.DEPARTMENT_STAFF_BUCKET,
    displayName: "DME Order to be signed",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.DME_ORDER,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.ADMIN_DISABILITY_FORMS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "asullivan243",
    displayName: "Disability/FMLA forms",
    documentLabel: "Request for Additional Information",
    documentTypeId: 390522,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.ADMIN_DISABILITY_FORMS,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.FINANCIAL_BILLING_RECORDS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "apina31",
    displayName: "Financial records",
    documentLabel: "Valerie Health - Financial Document",
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.FINANCIAL_BILLING_RECORDS,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.IMAGING_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "fidneedsmrifuappt",
    displayName: "Imaging results",
    documentLabel: "{{ imagingType }}",
    folder: "Imaging/Diagnostic Result",
    folderMapping: "Imaging/Diagnostic Result",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.IMAGING_RESULTS,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.LAB_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "fidneedslabs",
    displayName: "Lab results",
    documentLabel: "Lab*",
    documentTypeId: 400537,
    folder: "Lab Results",
    folderMapping: "Lab Results",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.LAB_RESULTS,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.LEGAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: FidAssignmentStrategy.DEPARTMENT_STAFF_BUCKET,
    displayName: "Power of attorney documents",
    documentLabel: "Health Care Power of Attorney",
    documentTypeId: 386202,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.LEGAL,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.MEDICAL_RECORD_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "chartmatemedrec",
    displayName: "Medical record requests",
    documentLabel: "Medical Record Request",
    documentTypeId: 271399,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.MEDICAL_RECORD_REQUEST,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.ORDER_LAB]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "fidneedslabs",
    displayName: "Lab orders",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.ORDER_LAB,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.ORDER_IMAGING]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: FidAssignmentStrategy.DEPARTMENT_STAFF_BUCKET,
    displayName: "Imaging orders",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.ORDER_IMAGING,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.CARE_PLAN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: FidAssignmentStrategy.DEPARTMENT_STAFF_BUCKET,
    displayName: "Plan of care",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.CARE_PLAN,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.PHARMACY_RELATED]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: FidAssignmentStrategy.DEPARTMENT_STAFF_BUCKET,
    displayName: "Any Rx document",
    documentLabel: "",
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.PHARMACY_RELATED,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: FidAssignmentStrategy.DEPARTMENT_STAFF_BUCKET,
    displayName: "Prior auth approval/denials medications",
    documentLabel: "Insurance {{ authorizationStatus }} - Medication",
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "fidneedsprocedure",
    displayName: "Prior auth approval/denials for procedures",
    documentLabel: "Insurance {{ authorizationStatus }} - Procedure",
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "fidneedspreufemri",
    displayName: "Prior auth approval/denials for imaging",
    documentLabel: "Insurance {{ authorizationStatus }} - Imaging",
    folder: "Lab/Imaging",
    folderMapping: "Lab/Imaging",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "fidneedssubmission",
    displayName: "Prior auth request for more information",
    documentLabel: "Request for additional information",
    documentTypeId: 390522,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.PROGRESS_NOTE_OPERATIVE_REPORT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: FidAssignmentStrategy.DEPARTMENT_STAFF_BUCKET,
    displayName: "Operative or procedural reports",
    documentLabel: "Operative Report",
    documentTypeId: 342771,
    folder: "Clinic Document - Operative Report",
    folderMapping: "Clinic Document - Operative Report",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.PROGRESS_NOTE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "fidneedslabs",
    displayName: "Any progress note/medical history/etc",
    documentLabel: "Historical Medical Record",
    folder: "Medical Record Document - Historical Medical Record",
    folderMapping: "Medical Record Document - Historical Medical Record",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.PROGRESS_NOTE,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.INSURANCE_CARD]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "fidneedsvob",
    displayName: "Insurance fax back request (verification of benefits information)",
    documentLabel: "Explanation of Benefits",
    documentTypeId: 390183,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.INSURANCE_CARD,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.MEDICAL_RECORD_REQUEST_HEDIS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "HEDIS & Risk Management",
    documentLabel: "Valerie Health Document",
    folder: null,
    folderMapping: null,
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.EXTERNAL_ROUTING_WF,
    classification: Classification.MEDICAL_RECORD_REQUEST_HEDIS,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.OTHER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Non patient information",
    documentLabel: "Valerie Health Document",
    folder: null,
    folderMapping: null,
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.EXTERNAL_ROUTING_WF,
    classification: Classification.OTHER,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.REFERRAL_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: FidAssignmentStrategy.DEPARTMENT_STAFF_BUCKET,
    displayName: "Requesting a referral from StrideCare FID",
    documentLabel: "Request for Additional Information",
    documentTypeId: 390522,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.REFERRAL_REQUEST,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.REFERRAL_STATUS_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: FidAssignmentStrategy.DEPARTMENT_STAFF_BUCKET,
    displayName: "Update on referral StrideCare FID sent",
    documentLabel: "Referral Acknowledgement",
    documentTypeId: 390844,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.STANDARD,
    classification: Classification.REFERRAL_STATUS_REQUEST,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.JUNK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Junk",
    documentLabel: "Junk",
    folder: null,
    folderMapping: null,
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    classification: Classification.JUNK,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
  [Classification.REFERRAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Referral",
    documentLabel: "New Patient Referral",
    documentTypeId: 391737,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: V2WorkflowType.INBOUND_REFERRAL_WF,
    classification: Classification.REFERRAL,
    group: ConfigSubdomain.STRIDECARE_FID,
  },
} as const;
